/**
 * Retry a function with exponential backoff
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: {
    maxRetries?: number;
    initialDelay?: number;
    maxDelay?: number;
    factor?: number;
    shouldRetry?: (error: any) => boolean;
    onRetry?: (attempt: number, delay: number, error: any) => void;
    infiniteRetryFor503?: boolean; // Keep retrying 503 errors indefinitely
  } = {}
): Promise<T> {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 30000,
    factor = 2,
    infiniteRetryFor503 = false,
    shouldRetry = (error) => {
      // Retry on 503 Service Unavailable or network errors
      if (error.message?.includes('503') || error.message?.includes('Service Unavailable')) {
        return true;
      }
      // Retry on 502 Bad Gateway
      if (error.message?.includes('502') || error.message?.includes('Bad Gateway')) {
        return true;
      }
      // Retry on 504 Gateway Timeout
      if (error.message?.includes('504') || error.message?.includes('Gateway Timeout')) {
        return true;
      }
      // Retry on network errors
      if (error.message?.includes('fetch failed') || error.message?.includes('network')) {
        return true;
      }
      // Retry on timeout errors
      if (error.message?.includes('timeout') || error.message?.includes('ETIMEDOUT')) {
        return true;
      }
      return false;
    },
    onRetry
  } = options;

  let lastError: any;
  let delay = initialDelay;

  let attempt = 0;
  
  // Use while loop for potentially infinite retries
  while (true) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      // Check if this is a 503 error
      const is503Error = error.message?.includes('503') || 
                         error.message?.includes('Service Unavailable') ||
                         error.status === 503;
      
      // For 503 errors with infinite retry enabled, keep going
      if (infiniteRetryFor503 && is503Error) {
        // Don't check maxRetries for 503 errors
      } else if (attempt >= maxRetries || !shouldRetry(error)) {
        // For other errors or when infinite retry is disabled, respect maxRetries
        throw error;
      }
      
      attempt++;

      // For 503 errors, use quick retries
      let currentDelay;
      if (is503Error) {
        // Quick retries for 503 - start at 1 second, cap at max delay
        currentDelay = Math.min(delay, maxDelay);
      } else {
        currentDelay = Math.min(delay, maxDelay);
      }
      
      // Add small jitter to prevent thundering herd (100-500ms)
      const jitteredDelay = currentDelay + Math.random() * 400 + 100;

      if (onRetry) {
        onRetry(attempt + 1, jitteredDelay, error);
      }

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, jitteredDelay));

      // Increase delay for next attempt
      delay *= factor;
    }
  }
}